import { css } from '@emotion/core'
import { usePageView } from '@fs/zion-analytics'
import { ContentAdd, ContentDelete } from '@fs/zion-icon/dist/cjs/icons'
import { useHistory } from '@fs/zion-router'
import {
  Button,
  Cell,
  colors,
  DataBlock,
  Grid,
  Row,
  Select,
  Separator,
  TextArea,
  TextField,
  H2,
  H3,
  H4,
  DangerButton,
} from '@fs/zion-ui'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEqual } from 'lodash'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import useTempleDirectory from '../../../hooks/useTempleDirectory'
import {
  getTempleInventoryProfiles,
  createTempleInventoryProfile,
  deleteTempleInventoryProfile,
  updateTempleInventoryProfile,
} from '../../../services/templeInventoryService'
import {
  convertDateString,
  GENEALOGICAL_DATE_FORMAT,
  NUMERIC_DATE_FORMAT,
  timestampToDateString,
} from '../../../util/DateUtil'
import { getIdElement, toNonNull } from '../../../util/StringUtil'
import { deepCopy, sleep } from '../../../util/Utils'
import DataView from '../../common/DataView'
import DmcTemplePicker from '../../common/DmcTemplePicker'
import TempleLabel from '../../common/TempleLabel'
import { headerCss } from '../../app/Title'

const languages = {
  Chinese: {
    code: 'zh',
    name: 'Chinese',
    orderOfName: 'SINOTYPIC',
  },
  Japanese: {
    code: 'ja-Hani',
    name: 'Japanese',
    orderOfName: 'SINOTYPIC',
  },
  Khmer: {
    code: 'km',
    name: 'Khmer',
    orderOfName: 'SINOTYPIC',
  },
  Korean: {
    code: 'ko-Hang',
    name: 'Korean',
    orderOfName: 'SINOTYPIC',
  },
  Latin: {
    code: 'und-Latn',
    name: 'Latin',
    orderOfName: '',
  },
  Mongolian: {
    code: 'mn-Cyrl',
    name: 'Mongolian',
    orderOfName: 'SINOTYPIC',
  },
  Russian: {
    code: 'ru',
    name: 'Russian',
    orderOfName: 'EUROTYPIC',
  },
  Thai: {
    code: 'th',
    name: 'Thai',
    orderOfName: 'SINOTYPIC',
  },
}

const enumToOptions = (enumeration) => {
  const options = Object.entries(enumeration).map((no) => {
    return {
      value: no[1],
      label: no[0],
    }
  })
  return options
}

const nameOrder = {
  Eurotypic: 'EUROTYPIC',
  Sinotypic: 'SINOTYPIC',
}
const nameOrderOptions = enumToOptions(nameOrder)

const paperSize = {
  Letter: 'LETTER',
  A4: 'A4',
}
const paperSizeOptions = enumToOptions(paperSize)

const templateLanguage = {
  Bulgarian: 'bg',
  Czech: 'cs',
  Danish: 'da',
  German: 'de',
  Greek: 'el',
  English: 'en',
  Esperanto: 'eo',
  Spanish: 'es',
  Estonian: 'et',
  Finnish: 'fi',
  Fijian: 'fj',
  French: 'fr',
  Croatian: 'hr',
  Haitian: 'ht',
  Hungarian: 'hu',
  Armenian: 'hy',
  Indonesian: 'id',
  Icelandic: 'is',
  Italian: 'it',
  Japanese: 'ja',
  Khmer: 'km',
  Korean: 'ko',
  Lithuanian: 'lt',
  Latvian: 'lv',
  Malagasy: 'mg',
  Macedonian: 'mk',
  Mongolian: 'mn',
  Malay: 'ms',
  Dutch: 'nl',
  Norwegian: 'no',
  Polish: 'pl',
  Portuguese: 'pt',
  Romanian: 'ro',
  Russian: 'ru',
  Slovak: 'sk',
  Slovenian: 'sl',
  Samoan: 'sm',
  Albanian: 'sq',
  Serbian: 'sr',
  Swedish: 'sv',
  Swahili: 'sw',
  Thai: 'th',
  Tonga: 'to',
  Turkish: 'tr',
  Ukrainian: 'uk',
  Vietnamese: 'vi',
  'Chinese (Simplified)': 'zh-Hans',
  Chinese: 'zh',
}
const templateLanguageOptions = enumToOptions(templateLanguage)
const NUM_RETRIES = 3

async function getTempleInventoryProfilesWithRetry(templeCode, retriesLeft = NUM_RETRIES) {
  try {
    return await getTempleInventoryProfiles(templeCode)
  } catch (err) {
    if (retriesLeft > 0 && err?.response?.status === 429) {
      await sleep(2 ** (NUM_RETRIES - retriesLeft) * 1000)
      return getTempleInventoryProfilesWithRetry(templeCode, retriesLeft - 1)
    }
    throw err
  }
}

export default function TempleInventory() {
  usePageView({ page_detail: 'DMConsole:TempleInventory' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.temple.inventory'))
  const { temples } = useTempleDirectory()
  const [selectedTemple, setSelectedTemple] = useState({})
  const history = useHistory()
  const templeCode =
    history?.location?.search
      ?.substring(1)
      .split('&')
      .find((item) => item.split('=')[0] === 'templeCode')
      ?.split('=')[1] || ''

  useEffect(() => {
    setSelectedTemple(temples?.[templeCode])
  }, [templeCode, temples])

  const onTempleChange = (selection) => {
    history.push(`/temple/inventory?templeCode=${selection.templeCode}`)
  }

  const onTempleClear = () => {
    history.push('/temple/inventory')
  }

  return (
    <>
      {temples && (
        <DmcTemplePicker
          templeCode={templeCode}
          temples={temples}
          onSuggestionSelected={(selection) => {
            selection && onTempleChange(selection)
          }}
          onClear={onTempleClear}
        />
      )}
      <Grid>
        <Cell columns={12}>
          {selectedTemple && (
            <H2 size="H3">
              <span css={headerCss}>{selectedTemple.name}</span>
            </H2>
          )}
          <TempleInformation temple={selectedTemple} />
          <Separator size="xxs" />
          {selectedTemple && selectedTemple.code && <TempleProfiles templeCode={selectedTemple.code} />}
        </Cell>
      </Grid>
    </>
  )
}

function TempleProfiles({ templeCode }) {
  const { t } = useTranslation()
  const { error, ok } = useNotifications()
  const [profiles, setProfiles] = useState()
  const [languageOptions, setLanguageOptions] = useState()
  const [defaultProfile, setDefaultProfile] = useState()
  const [adding, setAdding] = useState(true)

  // scriptType is not used, but the backend still needs it
  const [newProfile, setNewProfile] = useState()

  const refreshTempleProfiles = useCallback(() => {
    setAdding(false)
    getTempleInventoryProfilesWithRetry(templeCode)
      .then((response) => {
        setProfiles(response)
        const langs = response
          ? Object.values(languages)
              .filter((language) => !response.some((profile) => profile.lang === language.code))
              .map((no) => ({ value: no.code, label: no.name }))
          : Object.values(languages).map((no) => ({ value: no.code, label: no.name }))
        setLanguageOptions(langs)

        const defProfile = {
          templeCode,
          lang: langs && langs[0] ? langs[0].code : '',
          nameOrderOverride: null,
          templateLang: templateLanguage.English,
          paperSize: paperSize.Letter,
          printedWeekCount: 1,
          averages: [
            { ordinanceType: 'BAPTISM', sex: 'MALE', category: 'TempleList', value: 0 },
            { ordinanceType: 'CONFIRMATION', sex: 'MALE', category: 'TempleList', value: 0 },
            { ordinanceType: 'INITIATORY', sex: 'MALE', category: 'TempleList', value: 0 },
            { ordinanceType: 'ENDOWMENT', sex: 'MALE', category: 'TempleList', value: 0 },
            { ordinanceType: 'SEALING_TO_PARENTS', sex: 'MALE', category: 'TempleList', value: 0 },
            { ordinanceType: 'BAPTISM', sex: 'FEMALE', category: 'TempleList', value: 0 },
            { ordinanceType: 'CONFIRMATION', sex: 'FEMALE', category: 'TempleList', value: 0 },
            { ordinanceType: 'INITIATORY', sex: 'FEMALE', category: 'TempleList', value: 0 },
            { ordinanceType: 'ENDOWMENT', sex: 'FEMALE', category: 'TempleList', value: 0 },
            { ordinanceType: 'SEALING_TO_PARENTS', sex: 'FEMALE', category: 'TempleList', value: 0 },
            { ordinanceType: 'SEALING_TO_SPOUSE', sex: null, category: 'TempleList', value: 0 },
          ],
        }
        setDefaultProfile(defProfile)
        setNewProfile(defProfile)
      })
      .catch(() => {
        setProfiles([])
      })
  }, [templeCode])

  useEffect(() => {
    refreshTempleProfiles()
  }, [refreshTempleProfiles])

  const createProfile = () => {
    createTempleInventoryProfile(newProfile)
      .then(() => {
        ok(t('success', 'Success'))
        refreshTempleProfiles()
        setNewProfile(defaultProfile)
      })
      .catch((err) => {
        const errMessage = err.response?.data?.notifications?.[0]?.msg || err.message
        error(
          `${t('inventory.errors.creatingTempleProfile', 'Error creating temple profile')}${
            errMessage ? `: ${errMessage}` : ''
          }`,
          err
        )
      })
  }

  return (
    <>
      <Row>
        <H3 size="H5" growX>
          {t('temple.inventory.profiles', 'Profiles')}
        </H3>
        {templeCode && !adding && (
          <Button Icon={ContentAdd} emphasis="medium" onClick={() => setAdding(true)}>
            {t('action.add', 'Add')}
          </Button>
        )}
        {templeCode && adding && (
          <Row>
            <Button emphasis="high" onClick={createProfile}>
              {t('action.create', 'Create')}
            </Button>
            <Button
              emphasis="medium"
              onClick={() => {
                setAdding(false)
                setNewProfile(defaultProfile)
              }}
            >
              {t('action.cancel', 'Cancel')}
            </Button>
          </Row>
        )}
      </Row>
      {adding && languageOptions && newProfile && (
        <>
          <Separator size="xxs" />
          <TempleProfile
            templeProfile={newProfile}
            refresh={refreshTempleProfiles}
            adding={adding}
            setAdding={setAdding}
            setNewProfile={setNewProfile}
            languageOptions={languageOptions}
          />
        </>
      )}
      {profiles &&
        languageOptions &&
        profiles.length > 0 &&
        profiles.map((profile) => {
          const profileLanguage = Object.values(languages).find((language) => language.code === profile.lang)
          let languageOptionsIncludingProfileOption = languageOptions
          if (!profileLanguage) {
            profile.error = `Invalid language: ${
              profile && profile.lang ? profile.lang : ''
            }. Please select a valid language and save the profile to fix it.`
          } else {
            const profileLanguageOption = {
              value: profileLanguage.code,
              label: profileLanguage.name,
            }
            languageOptionsIncludingProfileOption = [profileLanguageOption, ...languageOptions]
          }

          return (
            <React.Fragment key={profile.id}>
              <Separator size="xxs" />
              {profile && (
                <TempleProfile
                  templeProfile={profile}
                  refresh={refreshTempleProfiles}
                  adding={adding}
                  setAdding={setAdding}
                  languageOptions={languageOptionsIncludingProfileOption}
                />
              )}
            </React.Fragment>
          )
        })}
    </>
  )
}

function TempleProfile({ templeProfile, refresh, adding = false, setAdding, setNewProfile, languageOptions }) {
  const { t } = useTranslation()
  const [profile, setProfile] = useState()
  const [dirty, setDirty] = useState(false)
  const { error, ok } = useNotifications()
  const errorBox = css`
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 0;
    margin-top: 1em;
    padding: 0.5em;
    border: 1px solid #d7d7d7;
    box-shadow: 0px 1px #bfbfbf;
    border-radius: 5px;
    background-color: ${colors.yellow20};
  `
  const language = useMemo(
    () =>
      languageOptions.filter((languageOption) => {
        return languageOption.value === templeProfile.lang
      }),
    [languageOptions, templeProfile.lang]
  )
  const ogProfile = {
    ...templeProfile,
    language: language && language.length > 0 ? language : '',
  }

  useEffect(() => {
    setProfile({
      ...templeProfile,
      language: language && language.length > 0 ? language : '',
    })
    setDirty(false)
  }, [templeProfile, adding, languageOptions, error, language])

  const handleChange = (updatedProfile) => {
    setDirty(!isEqual(ogProfile, updatedProfile))
    setProfile(updatedProfile)
    if (setNewProfile) setNewProfile(updatedProfile)
  }

  const saveProfile = () => {
    updateTempleInventoryProfile(profile.templeCode, profile.lang, profile)
      .then(() => {
        refresh()
        ok(
          t('temple.inventory.profiles.updateSuccess', '{lang} profile updated', {
            lang: profile.language?.[0]?.label,
          })
        )
      })
      .catch((err) => {
        error(t('inventory.errors.updatingTempleProfile', 'Error updating temple profile'), err)
      })
  }

  const resetProfile = () => {
    setProfile(ogProfile)
    setDirty(false)
  }

  const deleteProfile = () => {
    deleteTempleInventoryProfile(profile.templeCode, profile.lang)
      .then(() => {
        setAdding(false)
        refresh()
        ok(
          t('temple.inventory.profiles.deleteSuccess', '{lang} profile deleted', {
            lang: profile.language[0].label,
          })
        )
      })
      .catch((err) => {
        error(t('inventory.errors.deletingTempleProfile', 'Error deleting temple profile'), err)
        setAdding(false)
        refresh()
      })
  }

  if (!profile) {
    return null
  }
  const heading = `${setNewProfile && !profile.language?.[0]?.label ? `${t('new', 'New')} ` : ''}${t(
    'profile',
    'Profile'
  )} ${profile.language?.[0]?.label || ''}`

  return (
    <>
      <Row>
        <H4 size="H5" growX supplementary>
          {heading}
        </H4>
        {dirty && (
          <Button emphasis="medium" onClick={resetProfile}>
            {t('action.reset', 'Reset')}
          </Button>
        )}

        {dirty && (
          <Button emphasis="high" onClick={saveProfile}>
            {t('action.save', 'Save')}
          </Button>
        )}
        {!setNewProfile && (
          <DangerButton id={`delete_${profile.language?.[0]?.label}`} Icon={ContentDelete} onClick={deleteProfile}>
            {t('action.delete', 'Delete')}
          </DangerButton>
        )}
      </Row>
      {profile.error && <Row css={errorBox}>{profile.error}</Row>}
      <Grid>
        <Cell columns={2}>
          <DataBlock
            heading={t('inventory.profileInformation', 'Profile Information')}
            data={
              <>
                <TextField disabled label={t('templeCode', 'Temple Code')} value={profile.templeCode} />
                <TextField
                  disabled
                  label={t('temple.inventory.templeProfileId', 'Profile Id')}
                  value={profile.id ? getIdElement(profile.id) : ''}
                />
                <TextArea
                  disabled
                  label={t('created', 'Created')}
                  value={
                    profile.createDate
                      ? timestampToDateString({
                          timestamp: profile.createDate,
                          withTime: true,
                        })
                      : ''
                  }
                  rows={2}
                />
                <TextArea
                  disabled
                  label={t('updated', 'Updated')}
                  value={
                    profile.createDate
                      ? timestampToDateString({
                          timestamp: profile.updateDate,
                          withTime: true,
                        })
                      : ''
                  }
                  rows={2}
                />
              </>
            }
          />
        </Cell>
        <Cell columns={2}>
          <DataBlock
            heading="Locale"
            data={
              <>
                <Select
                  id="language-options"
                  name="language-options"
                  label={t('language.label', 'Language')}
                  placeholder={t('language.select', 'Select Language')}
                  options={languageOptions}
                  value={profile.lang}
                  onChange={(e) => {
                    handleChange({
                      ...profile,
                      lang: e.target.value,
                    })
                  }}
                />
                <Select
                  id="name-order-options"
                  name="name-order-options"
                  label={t('inventory.nameOrderoverride', 'Name Order Override')}
                  placeholder={t('none', 'None')}
                  options={nameOrderOptions}
                  value={profile.nameOrderOverride}
                  onChange={(e) =>
                    handleChange({
                      ...profile,
                      nameOrderOverride: e.target.value || null,
                    })
                  }
                />
                <Select
                  id="template-language-options"
                  name="template-language-options"
                  label={t('inventory.templateLanguage', 'Template Language')}
                  hidePlaceholder
                  options={templateLanguageOptions}
                  value={profile.templateLang}
                  onChange={(e) =>
                    handleChange({
                      ...profile,
                      templateLang: e.target.value,
                    })
                  }
                />
                <Select
                  id="paper-size-options"
                  name="paper-size-options"
                  label={t('inventory.paperSize', 'Paper Size')}
                  hidePlaceholder
                  options={paperSizeOptions}
                  value={profile.paperSize}
                  onChange={(e) => {
                    handleChange({
                      ...profile,
                      paperSize: e.target.value,
                    })
                  }}
                />
              </>
            }
          />
        </Cell>
        <Cell columns={2}>
          <DataBlock
            heading={t('inventory.limits', 'Limits')}
            data={
              <TextField
                id={`printedWeekCountInput_${profile.language === '' ? 'new' : profile.language?.[0]?.label}`}
                label={t('temple.inventory.printedWeekCount', 'Printed Week Count')}
                value={`${profile.printedWeekCount}`}
                onChange={(e) => {
                  const valueAsInt = parseInt(e.target.value, 10)
                  handleChange({
                    ...profile,
                    printedWeekCount: Number.isNaN(valueAsInt) ? '' : valueAsInt,
                  })
                }}
                errorMessage={profile.printedWeekCount < 1 && t('errors.mustBeGreaterThan1', 'Must be greater than 1')}
              />
            }
          />
        </Cell>
        <Cell columns={2}>
          <DataBlock
            heading={t('inventory.femaleAverages', 'Female Averages')}
            data={
              <>
                <TextField
                  label={t('temple.inventory.baptismCount', 'Baptisms')}
                  value={`${
                    profile.averages.find((item) => item.ordinanceType === 'BAPTISM' && item.sex === 'FEMALE').value
                  }`}
                  onChange={(e) => {
                    const profileAverages = deepCopy(profile.averages)
                    const averageToEdit = profileAverages.find(
                      (item) => item.ordinanceType === 'BAPTISM' && item.sex === 'FEMALE'
                    )
                    const valueAsInt = parseInt(e.target.value, 10)
                    averageToEdit.value = Number.isNaN(valueAsInt) ? '' : valueAsInt
                    handleChange({
                      ...profile,
                      averages: profileAverages,
                    })
                  }}
                />
                <TextField
                  label={t('temple.inventory.confirmationCount', 'Confirmations')}
                  value={`${
                    profile.averages.find((item) => item.ordinanceType === 'CONFIRMATION' && item.sex === 'FEMALE')
                      .value
                  }`}
                  onChange={(e) => {
                    const profileAverages = deepCopy(profile.averages)
                    const averageToEdit = profileAverages.find(
                      (item) => item.ordinanceType === 'CONFIRMATION' && item.sex === 'FEMALE'
                    )
                    const valueAsInt = parseInt(e.target.value, 10)
                    averageToEdit.value = Number.isNaN(valueAsInt) ? '' : valueAsInt
                    handleChange({
                      ...profile,
                      averages: profileAverages,
                    })
                  }}
                />
                <TextField
                  label={t('temple.inventory.initiatoryCount', 'Initiatories')}
                  value={`${
                    profile.averages.find((item) => item.ordinanceType === 'INITIATORY' && item.sex === 'FEMALE').value
                  }`}
                  onChange={(e) => {
                    const profileAverages = deepCopy(profile.averages)
                    const averageToEdit = profileAverages.find(
                      (item) => item.ordinanceType === 'INITIATORY' && item.sex === 'FEMALE'
                    )
                    const valueAsInt = parseInt(e.target.value, 10)
                    averageToEdit.value = Number.isNaN(valueAsInt) ? '' : valueAsInt
                    handleChange({
                      ...profile,
                      averages: profileAverages,
                    })
                  }}
                />
                <TextField
                  label={t('temple.inventory.endowmentCount', 'Endowments')}
                  value={`${
                    profile.averages.find((item) => item.ordinanceType === 'ENDOWMENT' && item.sex === 'FEMALE').value
                  }`}
                  onChange={(e) => {
                    const profileAverages = deepCopy(profile.averages)
                    const averageToEdit = profileAverages.find(
                      (item) => item.ordinanceType === 'ENDOWMENT' && item.sex === 'FEMALE'
                    )
                    const valueAsInt = parseInt(e.target.value, 10)
                    averageToEdit.value = Number.isNaN(valueAsInt) ? '' : valueAsInt
                    handleChange({
                      ...profile,
                      averages: profileAverages,
                    })
                  }}
                />
                <TextField
                  label={t('temple.inventory.sealingToParentsCount', 'Sealings to Parents')}
                  value={`${
                    profile.averages.find(
                      (item) => item.ordinanceType === 'SEALING_TO_PARENTS' && item.sex === 'FEMALE'
                    ).value
                  }`}
                  onChange={(e) => {
                    const profileAverages = deepCopy(profile.averages)
                    const averageToEdit = profileAverages.find(
                      (item) => item.ordinanceType === 'SEALING_TO_PARENTS' && item.sex === 'FEMALE'
                    )
                    const valueAsInt = parseInt(e.target.value, 10)
                    averageToEdit.value = Number.isNaN(valueAsInt) ? '' : valueAsInt
                    handleChange({
                      ...profile,
                      averages: profileAverages,
                    })
                  }}
                />
              </>
            }
          />
        </Cell>
        <Cell columns={2}>
          <DataBlock
            heading={t('inventory.maleAverages', 'Male Averages')}
            data={
              <>
                <TextField
                  label={t('temple.inventory.baptismCount', 'Baptisms')}
                  value={`${
                    profile.averages.find((item) => item.ordinanceType === 'BAPTISM' && item.sex === 'MALE').value
                  }`}
                  onChange={(e) => {
                    const profileAverages = deepCopy(profile.averages)
                    const averageToEdit = profileAverages.find(
                      (item) => item.ordinanceType === 'BAPTISM' && item.sex === 'MALE'
                    )
                    const valueAsInt = parseInt(e.target.value, 10)
                    averageToEdit.value = Number.isNaN(valueAsInt) ? '' : valueAsInt
                    handleChange({
                      ...profile,
                      averages: profileAverages,
                    })
                  }}
                />
                <TextField
                  label={t('temple.inventory.confirmationCount', 'Confirmations')}
                  value={`${
                    profile.averages.find((item) => item.ordinanceType === 'CONFIRMATION' && item.sex === 'MALE').value
                  }`}
                  onChange={(e) => {
                    const profileAverages = deepCopy(profile.averages)
                    const averageToEdit = profileAverages.find(
                      (item) => item.ordinanceType === 'CONFIRMATION' && item.sex === 'MALE'
                    )
                    const valueAsInt = parseInt(e.target.value, 10)
                    averageToEdit.value = Number.isNaN(valueAsInt) ? '' : valueAsInt
                    handleChange({
                      ...profile,
                      averages: profileAverages,
                    })
                  }}
                />
                <TextField
                  label={t('temple.inventory.initiatoryCount', 'Initiatories')}
                  value={`${
                    profile.averages.find((item) => item.ordinanceType === 'INITIATORY' && item.sex === 'MALE').value
                  }`}
                  onChange={(e) => {
                    const profileAverages = deepCopy(profile.averages)
                    const averageToEdit = profileAverages.find(
                      (item) => item.ordinanceType === 'INITIATORY' && item.sex === 'MALE'
                    )
                    const valueAsInt = parseInt(e.target.value, 10)
                    averageToEdit.value = Number.isNaN(valueAsInt) ? '' : valueAsInt
                    handleChange({
                      ...profile,
                      averages: profileAverages,
                    })
                  }}
                />
                <TextField
                  label={t('temple.inventory.endowmentCount', 'Endowments')}
                  value={`${
                    profile.averages.find((item) => item.ordinanceType === 'ENDOWMENT' && item.sex === 'MALE').value
                  }`}
                  onChange={(e) => {
                    const profileAverages = deepCopy(profile.averages)
                    const averageToEdit = profileAverages.find(
                      (item) => item.ordinanceType === 'ENDOWMENT' && item.sex === 'MALE'
                    )
                    const valueAsInt = parseInt(e.target.value, 10)
                    averageToEdit.value = Number.isNaN(valueAsInt) ? '' : valueAsInt
                    handleChange({
                      ...profile,
                      averages: profileAverages,
                    })
                  }}
                />
                <TextField
                  label={t('temple.inventory.sealingToParentsCount', 'Sealings to Parents')}
                  value={`${
                    profile.averages.find((item) => item.ordinanceType === 'SEALING_TO_PARENTS' && item.sex === 'MALE')
                      .value
                  }`}
                  onChange={(e) => {
                    const profileAverages = deepCopy(profile.averages)
                    const averageToEdit = profileAverages.find(
                      (item) => item.ordinanceType === 'SEALING_TO_PARENTS' && item.sex === 'MALE'
                    )
                    const valueAsInt = parseInt(e.target.value, 10)
                    averageToEdit.value = Number.isNaN(valueAsInt) ? '' : valueAsInt
                    handleChange({
                      ...profile,
                      averages: profileAverages,
                    })
                  }}
                />
              </>
            }
          />
        </Cell>
        <Cell columns={2}>
          <DataBlock
            heading={t('inventory.spouseAverages', 'Spouse Averages')}
            data={
              <TextField
                label={t('temple.inventory.sealingToSpouseCount', 'Sealings to Spouse')}
                value={`${
                  profile.averages.find((item) => item.ordinanceType === 'SEALING_TO_SPOUSE' && item.sex === null).value
                }`}
                onChange={(e) => {
                  const profileAverages = deepCopy(profile.averages)
                  const averageToEdit = profileAverages.find(
                    (item) => item.ordinanceType === 'SEALING_TO_SPOUSE' && item.sex === null
                  )
                  const valueAsInt = parseInt(e.target.value, 10)
                  averageToEdit.value = Number.isNaN(valueAsInt) ? '' : valueAsInt
                  handleChange({
                    ...profile,
                    averages: profileAverages,
                  })
                }}
              />
            }
          />
        </Cell>
      </Grid>
    </>
  )
}

function TempleInformation({ temple }) {
  const { t } = useTranslation()
  if (!temple || !temple.code) {
    return null
  }

  const templeDedicationDate = Array.isArray(temple.dedicationDate)
    ? `${temple.dedicationDate?.[0]}-${temple.dedicationDate?.[1]}-${temple.dedicationDate?.[2]}`
    : temple.dedicationDate

  const templeOrdinancesStartedOn = Array.isArray(temple.ordinancesStartedOn)
    ? `${temple.ordinancesStartedOn?.[0]}-${temple.ordinancesStartedOn?.[1]}-${temple.ordinancesStartedOn?.[2]}`
    : temple.ordinancesStartedOn

  const templeInformationData = [
    {
      label: t('templeCode', 'Temple Code'),
      detail: <TempleLabel temple={temple} />,
    },
    {
      label: t('location', 'Location'),
      detail: toNonNull(temple.location),
    },
    {
      label: t('temple.inventory.dedicatedOn', 'Dedicated'),
      detail: temple.dedicationDate
        ? convertDateString(templeDedicationDate, NUMERIC_DATE_FORMAT, GENEALOGICAL_DATE_FORMAT)
        : '',
    },
    {
      label: t('temple.inventory.templeName', 'Name'),
      detail: toNonNull(temple.name),
    },
    {
      label: t('temple.inventory.timeZone', 'Timezone'),
      detail: toNonNull(temple.timeZone),
    },
    {
      label: t('temple.inventory.ordinancesStartedOn', 'Ordinances Started'),
      detail: temple.ordinancesStartedOn
        ? convertDateString(templeOrdinancesStartedOn, NUMERIC_DATE_FORMAT, GENEALOGICAL_DATE_FORMAT)
        : '',
    },
    {
      label: t('temple.inventory.district', 'District'),
      detail: toNonNull(temple.district),
    },
  ]
  return <DataView data={templeInformationData} />
}
